import { cloneDeep } from "lodash";
import { IFlightMarker } from "../../../apiFacades/bapi/flightMarkers";
import { ISRS } from "../../../apiFacades/csu/site";
import { Err } from "../../../error";
import { compareNails } from "./utils";

// Interface for INailList
export interface IFlightMarkersList {
  nails: IFlightMarker[];
} // END INailsList

// Interface for coordinates, contains x, y, z
// @field x: x coordinate, or longitude in case of WGS84
// @field y: y coordinate, or latitude in case of WGS84
// @field z: z coordinate, or altitude in case of WGS84
export interface ICoords {
  x: number;
  y: number;
  z: number;
} // END ICoords
/**
 * Interface for request to add new GCP/QC
 */
export interface IAddFlightMarkerRequest {
  localCoords: ICoords;
  name: string;
  isQC: boolean;
  operatorName: string;
  operatorCS: ISRS;
} // END IAddNailRequest

export interface IFlightMarkersLoadPayload {
  customerId: string;
  siteId: string;
} // END INailsLoadPayload

export interface IFlightMarkersSavePayload {
  customerId: string;
  siteId: string;
  nailsList: IFlightMarkersList;
} // END INailsSavePayload

// #region ACTIONS

// TODO: rename NAILS to FLIGHT_MARKERS after sagas and computables will be dropped, disable exports
export const NAILS_LOAD_FULFILLED = "NAILS_LOAD_FULFILLED";
export const persistFlightMarkers = (flightMarkersList: IFlightMarker[]) => ({
  type: NAILS_LOAD_FULFILLED,
  payload: {
    nails: flightMarkersList.sort(
      (nail1: IFlightMarker, nail2: IFlightMarker) => {
        return compareNails(nail1, nail2);
      },
    ),
  },
});

export const NAILS_LOAD_REJECTED = "NAILS_LOAD_REJECTED";

export const NAILS_SAVE_FULFILLED = "NAILS_SAVE_FULFILLED";
export const NAILS_SAVE_REJECTED = "NAILS_SAVE_REJECTED";
export const NAILS_SAVE_REQUESTED = "NAILS_SAVE_REQUESTED";

export const NAILS_NAIL_TOQC_CONVERTED = "NAILS_NAIL_TOQC_CONVERTED";
export const NAILS_NAIL_TOGCP_CONVERTED = "NAILS_NAIL_TOGCP_CONVERTED";
export const NAILS_NAIL_ARCHIVED = "NAILS_NAIL_ARCHIVED";
export const NAILS_NAIL_UNARCHIVED = "NAILS_NAIL_UNARCHIVED";
export const NAILS_NAIL_RENAMED = "NAILS_NAIL_RENAMED";
export const NAILS_UPDATED = "NAILS_UPDATED";

export const NAILS_WAS_RESET = "NAILS_WAS_RESET";

export const requestFlightMarkersSave = (
  customerId: string,
  siteId: string,
  nailsList: IFlightMarkersList,
): IAction<IFlightMarkersSavePayload> => ({
  payload: { customerId, siteId, nailsList },
  type: NAILS_SAVE_REQUESTED,
});

export const convertFlightMarkerToGcp = (nailId: string): IAction<string> => ({
  payload: nailId,
  type: NAILS_NAIL_TOGCP_CONVERTED,
});

export const convertFlightMarkerToQc = (nailId: string): IAction<string> => ({
  payload: nailId,
  type: NAILS_NAIL_TOQC_CONVERTED,
});

export const archiveFlightMarker = (
  nailId: string,
  email: string,
): IAction<{ nailId: string; email: string }> => ({
  payload: { nailId, email },
  type: NAILS_NAIL_ARCHIVED,
});

export const unarchiveFlightMarker = (nailId: string): IAction<string> => ({
  payload: nailId,
  type: NAILS_NAIL_UNARCHIVED,
});

export const updateNails = (
  proposedFlightMarkers: IFlightMarkersList,
): IAction<IFlightMarkersList> => ({
  payload: proposedFlightMarkers,
  type: NAILS_UPDATED,
});

interface IRenameFlightMarkerParams {
  id: string;
  newName: string;
}
export const renameFlightMarker = (
  id: string,
  newName: string,
): IAction<IRenameFlightMarkerParams> => ({
  payload: { id, newName },
  type: NAILS_NAIL_RENAMED,
});

export const resetFlightMarkersState = (): IAction<void> => ({
  payload: null,
  type: NAILS_WAS_RESET,
});

// #endregion ACTIONS

// #region REDUCER

export interface IFlightMarkersState {
  pending: boolean;
  data: IFlightMarkersList | null;
  error: Err;
}

const defaultState: IFlightMarkersState = {
  pending: false,
  data: null,
  error: null,
};

export default function flightMarkersReducer(
  state: IFlightMarkersState = defaultState,
  action: IAction<any>,
): IFlightMarkersState {
  switch (action.type) {
    case NAILS_SAVE_REQUESTED:
      return {
        ...state,
        pending: true,
      };
    case NAILS_LOAD_FULFILLED:
    case NAILS_SAVE_FULFILLED:
      return {
        ...defaultState,
        data: action.payload,
      };
    case NAILS_LOAD_REJECTED:
    case NAILS_SAVE_REJECTED:
      return {
        ...defaultState,
        error: action.payload,
      };
    case NAILS_UPDATED:
      return {
        ...defaultState,
        data: action.payload,
      };
    case NAILS_NAIL_RENAMED:
      return {
        ...defaultState,
        data: mRenameFlightMarker(
          state.data,
          action.payload.id,
          action.payload.newName,
        ),
      };
    case NAILS_NAIL_ARCHIVED:
      return {
        ...defaultState,
        data: mArchiveFlightMarker(state.data, action.payload),
      };
    case NAILS_NAIL_UNARCHIVED:
      return {
        ...defaultState,
        data: mUnarchiveFlightMarker(state.data, action.payload),
      };
    case NAILS_NAIL_TOGCP_CONVERTED:
      return {
        ...defaultState,
        data: mConvertFlightMarkerToGcp(state.data, action.payload),
      };
    case NAILS_NAIL_TOQC_CONVERTED:
      return {
        ...defaultState,
        data: mConvertFlightMarkerToQc(state.data, action.payload),
      };
    case NAILS_WAS_RESET:
      return defaultState;
    default:
      return state;
  }
}

// #endregion REDUCER

// #region MODIFIERS

function mRenameFlightMarker(
  flightMarkersList: IFlightMarkersList,
  flightMarkerId: string,
  newName: string,
): IFlightMarkersList {
  const newList = cloneDeep(flightMarkersList);
  const nail = newList.nails.find(({ id }) => id === flightMarkerId);
  nail.name = newName;
  return newList;
}

export function mArchiveFlightMarker(
  nailsList: IFlightMarkersList,
  { nailId, email }: { nailId: string; email: string },
): IFlightMarkersList {
  const newNailsList = cloneDeep(nailsList);
  const nail = findFlightMarkerByIdInList(newNailsList, nailId);
  if (nail) {
    nail.archivedBy = email;
    nail.tsArchived = new Date().getTime();
  }
  return newNailsList;
}

export function mUnarchiveFlightMarker(
  nailsList: IFlightMarkersList,
  nailId: string,
): IFlightMarkersList {
  const newNailsList = cloneDeep(nailsList);
  const nail = findFlightMarkerByIdInList(newNailsList, nailId);
  if (nail) {
    nail.tsArchived = null;
  }
  return newNailsList;
}

export function mConvertFlightMarkerToQc(
  nailsList: IFlightMarkersList,
  nailId: string,
): IFlightMarkersList {
  const newNailsList = cloneDeep(nailsList);
  const nail = findFlightMarkerByIdInList(newNailsList, nailId);
  if (nail) {
    nail.isQC = true;
  }
  return newNailsList;
}

export function mConvertFlightMarkerToGcp(
  nailsList: IFlightMarkersList,
  nailId: string,
): IFlightMarkersList {
  const newNailsList = cloneDeep(nailsList);
  const nail = findFlightMarkerByIdInList(newNailsList, nailId);
  if (nail) {
    nail.isQC = false;
  }
  return newNailsList;
}

// #endregion MODIFIERS

export const getFlightMarkersList = (
  state: IFlightMarkersState,
): IFlightMarker[] => (state.data ? state.data.nails : null);

export const getFlightMarkerById = (
  state: IFlightMarkersState,
  nailId: string,
): IFlightMarker =>
  state.data &&
  state.data.nails &&
  state.data.nails.find(({ id }) => id === nailId);

function findFlightMarkerByIdInList(
  nailsList: IFlightMarkersList,
  nailId: string,
): IFlightMarker {
  return nailsList.nails.find(({ id }) => id === nailId) || null;
} // END findNailByID

export const isActiveQC = ({ isQC, tsArchived }: IFlightMarker): boolean =>
  isQC && !tsArchived;
export const isActiveGCP = ({ isQC, tsArchived }: IFlightMarker): boolean =>
  !isQC && !tsArchived;
export const isArchived = ({ tsArchived }: IFlightMarker): boolean =>
  !!tsArchived;

export const getActiveGcpFlightMarkers = (
  state: IFlightMarkersState,
): IFlightMarker[] => state.data.nails.filter(isActiveGCP);
export const getActiveQcFlightMarkers = (
  state: IFlightMarkersState,
): IFlightMarker[] => state.data.nails.filter(isActiveQC);
export const getArchivedFlightMarkers = (
  state: IFlightMarkersState,
): IFlightMarker[] => state.data?.nails?.filter(isArchived) ?? [];
